import { useQuery } from "react-query";
import { useSnackbar } from "@kitbird/core";
import { useEffect, useState } from "react";
import { CreateUpdateModalType, IOptionItem } from "../../../../typings/common";
import { toNumber } from "../../../../utils/common";
import useAuth from "../../../../components/Authentication/useAuth";
import { getUserPosition } from "../../../../services/userPositionService/userPositionService";
import { IGetUserPositionData } from "../../../../services/userPositionService/types";
import { listCodeTypeAll } from "../constants";
import { postTrx } from "../../../../services/trxService";
import { IGetGLAccountsData } from "../../../../services/trxCodeSetService";
import { IGetVendorAccountsData } from "../../../../services/vendorSetService";
import { getRedReceipt } from "../../../../services/bmAgingService";
import { getTrxRedReceipts } from "../../../../services/trxRedReceiptService";
import useFormHook, { RedReceiptType } from "./useFormHook";
import useTableHook from "./useTableHook";
import useFileHook from "./useFileHook";
import { generateFileList, generatePostTrxPayload } from "./utils";
import usePrintHook from "./usePrintHook";
import { getPools } from "../../../../services/poolService";
import { getAreas } from "../../../../services/areaService";

const useCreateReceiptSubmission = () => {
  const [isCreateEditModal, setCreateEditModal] = useState<
    CreateUpdateModalType | false
  >(false);
  const [dropdownUserType, setdropdownUserType] = useState<IOptionItem[]>([]);
  const [dropdownArea, setdropdownArea] = useState<IOptionItem[]>([]);
  const [dropdownPool, setdropdownPool] = useState<IOptionItem[]>([]);
  const [dataUser, setDataUser] = useState<Partial<IGetUserPositionData>>({});
  const [isLoading, setLoading] = useState(false);
  const { userInfo } = useAuth();
  const snackbar = useSnackbar();
  const [departmentOpts, setDepartmentOpts] = useState<IOptionItem[]>([]);
  const [trxTypeOpts, setTrxTypeOpts] = useState<IOptionItem[]>([]);
  const [segmentOpts, setSegmentOpts] = useState<IOptionItem[]>([]);
  const [companyOpts, setCompanyOpts] = useState<IOptionItem[]>([]);
  const [companyRelationOpts, setCompanyRelationOpts] = useState<IOptionItem[]>([]);
  const [glAccountOpts, setGLAccountOpts] = useState<IGetGLAccountsData[]>([]);
  const [vendorAccountOpts, setVendorAccountOpts] = useState<IGetVendorAccountsData[]>([]);
  const [redReceiptTypeOpts, setRedReceiptTypeOpts] = useState<RedReceiptType[]>([]);
  const [isPrintModal, setPrintModal] = useState(false);

  const { printMethods } = usePrintHook();

  const {
    uploadedMedia,
    setUploadedMedia,
    processFiles,
    onFileBrowse,
    doDeleteFileIndex,
  } = useFileHook();

  const {
    isCreator,
    isApprover,
    trxParams,
    setTrxParams,
    searchText,
    setSearchText,
    dataPoolsDepartments,
    isPoolsDepartmentsLoading,
    meta,
    setMeta,
    fetchCount,
    setFetchCount,
    pageType,
    setPageType,
    tab,
    setTab,
    columnOpts,
    setColumnOpts,
    hiddenColumn,
    dataAllDepartment,
    seeAllDepartmentOpt,
    currPoolOpts,
    currDepartmentOpts,
    setCurrDepartmentOpts,
    dataCompanies,
    isCompaniesLoading,

    transactionQuery,
    handleDownloadCsv,
    handleDownloadPdf,
    seeAllCompanyOpt,
  } = useTableHook(setLoading);

  const {
    data: transactionPages,
    isFetching,
    isFetchingNextPage,
    refetch: refetchTable,
    fetchNextPage,
    remove,
  } = transactionQuery;

  const {
    glAccount,
    setGlAccount,
    destinationAccount,
    setDestinationAccount,
    redReceiptType,
    setRedReceiptType,
    redReceiptNumber,
    setRedReceiptNumber,
    initialValueCharge,
    methods,
  } = useFormHook(dataUser);
  const {
    getValues, handleSubmit, reset, setValue, watch,
  } = methods;

  const { data: dataUserInfo } = useQuery({
    queryKey: "get_user_position",
    queryFn: () => getUserPosition(userInfo.email),
  });

  const { data: dataPools } = useQuery({
    queryKey: "get_pools",
    queryFn: getPools,
  });

  const { data: dataAreas } = useQuery({
    queryKey: "get_areas",
    queryFn: getAreas,
  });

  const { data: dataRedReceipt } = useQuery({
    queryKey: "get_red_receipt",
    queryFn: () => getRedReceipt(),
  });

  const { data: dataTrxRedReceipt } = useQuery({
    queryKey: ["get_trx_red_receipt",
      watch("pool.value"),
      watch("department"),
      watch("codeType.value"),
      watch("area.value"),
    ],
    enabled: !!(getValues("pool.value")
      && getValues("department")
      && getValues("codeType.value")
      && getValues("area.value")),
    queryFn: () => getTrxRedReceipts({
      pool_id: getValues("pool.value") as string,
      department: getValues("department") as string,
      code_type: getValues("codeType.value") as string,
      area_id: getValues("area.value") as string,
    }),
  });

  useEffect(() => {
    if (dataRedReceipt?.listSmplbmaging) {
      setRedReceiptTypeOpts(dataRedReceipt?.listSmplbmaging.map((item) => (
        { ...item, ijirType: "Ijir", ijirExpiryDesc: `Dilakukan ${item.aging_interval} hari setelah dana diterima` }
      )));
    }
  }, [dataRedReceipt]);

  const doSetDatauser = () => {
    setDataUser((prev) => ({
      ...prev,
      employee_id: dataUserInfo?.lstmstuserpostition[0]?.employee_id,
      employee_name: dataUserInfo?.lstmstuserpostition[0]?.employee_name,
      job_title: dataUserInfo?.lstmstuserpostition[0]?.job_title,
      job_id: dataUserInfo?.lstmstuserpostition[0]?.job_id,
    }));
    setValue("employeeNip", dataUserInfo?.lstmstuserpostition[0]?.employee_id || "");
    setValue("employeeName", dataUserInfo?.lstmstuserpostition[0]?.employee_name || "");
    setValue("jobTitle", dataUserInfo?.lstmstuserpostition[0]?.job_title || "");
    setValue("jobId", dataUserInfo?.lstmstuserpostition[0]?.job_id || 0);
  };

  const doSetDropdownTypeUser = () => {
    const codeTypes = dataUserInfo
      ? Array.from(new Set(dataUserInfo.lstmstuserpostition.map((item) => item.code_type)))
      : [];
    const codeTypeOpts: IOptionItem[] = codeTypes.map((code) => {
      const codeTypeItem = listCodeTypeAll.find((codeType) => codeType.code_type === code);
      return {
        label: codeTypeItem?.code_type_desc || "",
        value: codeTypeItem?.code_type || "",
      };
    });
    setdropdownUserType(codeTypeOpts);
    if (codeTypeOpts.length === 1) {
      setValue("codeType", codeTypeOpts[0]);
      printMethods.setValue("codeType", codeTypeOpts[0]);
    }
  };

  const doSetDropdownArea = () => {
    const areas = dataUserInfo
      ? Array.from(new Set(dataUserInfo.lstmstuserpostition.map((item) => item.area_id)))
      : [];
    const allArea = dataAreas?.listareaall || [];
    const areaOpts: IOptionItem[] = areas.map((id) => {
      const areaItem = allArea.find((area) => area.id === id);
      return {
        label: areaItem?.name || "",
        value: areaItem?.id || "",
      };
    });
    setdropdownArea(areaOpts);
    if (areaOpts.length === 1) {
      setValue("area", areaOpts[0]);
      printMethods.setValue("area", areaOpts[0]);
    }
  };

  const doSetDropdownPool = () => {
    const pools = dataUserInfo
      ? Array.from(new Set(dataUserInfo.lstmstuserpostition.map((item) => item.pool_id)))
      : [];
    const allPools = dataPools?.list_pool || [];
    const poolOpts: IOptionItem[] = pools.map((id) => {
      const poolItem = allPools.find((pool) => pool.pool_id === id);
      return {
        label: poolItem?.pool_name || "",
        value: poolItem?.pool_id || "",
      };
    });
    setdropdownPool(poolOpts);
    if (poolOpts.length === 1) {
      setValue("pool", poolOpts[0]);
      printMethods.setValue("pool", poolOpts[0]);
    }
  };

  const initFetch = () => {
    doSetDatauser();
    doSetDropdownTypeUser();
    doSetDropdownArea();
    doSetDropdownPool();
    setUploadedMedia([]);
  };

  useEffect(() => {
    if (dataUserInfo && dataAreas && dataPools) {
      initFetch();
    }
  }, [dataUserInfo, dataAreas, dataPools]);

  const handleSetTipeUser = (e: IOptionItem) => {
    setValue("codeType", e);
  };

  const handleSetWilayah = (e: IOptionItem) => {
    setValue("area", e);
  };

  const handleSetPool = (e: IOptionItem) => {
    setValue("pool", e);
  };

  const handleSetAmount = (e: React.ChangeEvent<HTMLInputElement> | undefined) => {
    const newValue = e ? toNumber(e.target.value) : "0";
    setValue("amount", newValue, { shouldValidate: true });
  };

  const handleSetPlanPostDate = (dates: Date | (Date | null)[] | null) => {
    if (Array.isArray(dates) && dates[0]) setValue("planPostDate", (dates[0].toISOString()), { shouldValidate: true });
  };

  const onSubmit = (onSuccess?: () => void) => {
    handleSubmit((data) => {
      const fileList = generateFileList(uploadedMedia, data);
      if (fileList.length > 0) {
        setLoading(true);
        postTrx(
          generatePostTrxPayload({ ...data, email: userInfo.email, fileList }),
        ).then((res) => {
          if (res.responseCode === "Succeed") {
            snackbar.show(res.responseMessage, "success");
            if (typeof onSuccess === "function") {
              onSuccess();
              reset(initialValueCharge);
              doSetDatauser();
              if (dataUserInfo) {
                doSetDatauser();
                doSetDropdownTypeUser();
                doSetDropdownArea();
                doSetDropdownPool();
              }
            }
          } else snackbar.show(res.responseMessage, "error");
        }).catch(() => {
          snackbar.show("Gagal simpan data", "error");
        }).finally(() => setLoading(false));
      } else snackbar.show("Gagal, harap isi lampiran", "error");
    })();
  };

  return {
    initialValueCharge,
    meta,
    setMeta,
    trxParams,
    setTrxParams,
    searchText,
    setSearchText,
    isCreateEditModal,
    setCreateEditModal,
    onFileBrowse,
    doDeleteFileIndex,
    uploadedMedia,
    setUploadedMedia,
    transactionPages,
    isFetching,
    isFetchingNextPage,
    refetchTable,
    fetchNextPage,
    remove,

    isCreator,
    isApprover,

    // dataUserAccount,
    dataTrxRedReceipt,
    dataPoolsDepartments,
    isPoolsDepartmentsLoading,

    glAccount,
    setGlAccount,
    destinationAccount,
    setDestinationAccount,
    redReceiptNumber,
    setRedReceiptNumber,
    redReceiptType,
    setRedReceiptType,
    columnOpts,
    setColumnOpts,
    trxTypeOpts,
    setTrxTypeOpts,
    departmentOpts,
    setDepartmentOpts,
    segmentOpts,
    setSegmentOpts,
    companyOpts,
    setCompanyOpts,
    companyRelationOpts,
    setCompanyRelationOpts,
    glAccountOpts,
    setGLAccountOpts,
    vendorAccountOpts,
    setVendorAccountOpts,
    redReceiptTypeOpts,
    isPrintModal,
    setPrintModal,
    dataAllDepartment,
    seeAllDepartmentOpt,
    currPoolOpts,
    currDepartmentOpts,
    setCurrDepartmentOpts,
    dataCompanies,
    isCompaniesLoading,

    pageType,
    setPageType,
    tab,
    setTab,
    fetchCount,
    isLoading,
    setLoading,
    hiddenColumn,
    dataUser,
    onSubmit,
    initFetch,
    handleSetTipeUser,
    handleSetWilayah,
    handleSetPool,
    handleSetAmount,
    handleSetPlanPostDate,
    methods,
    dropdownUserType,
    dropdownArea,
    dropdownPool,
    dataUserInfo,
    handleDownloadCsv,
    handleDownloadPdf,
    printMethods,
    seeAllCompanyOpt,
  };
};

export default useCreateReceiptSubmission;
